import { normalizeRect } from './annotationMath'

const DEFAULT_FONT_ASCENT = 0.8
const DEFAULT_FONT_DESCENT = -0.2
const HIT_PADDING_POINTS = 1

let measurementContext

function measuredOffsets(text, startOffset, endOffset, fontStyle) {
  try {
    if (!measurementContext && typeof document !== 'undefined') {
      measurementContext = document.createElement('canvas').getContext('2d')
    }
    if (measurementContext) {
      measurementContext.font = `100px ${fontStyle?.fontFamily || 'sans-serif'}`
      const total = measurementContext.measureText(text).width
      if (total > 0) {
        return {
          startRatio: measurementContext.measureText(text.slice(0, startOffset)).width / total,
          endRatio: measurementContext.measureText(text.slice(0, endOffset)).width / total
        }
      }
    }
  } catch {
    // Fall through to character ratios when canvas measurement is unavailable.
  }
  return {
    startRatio: startOffset / text.length,
    endRatio: endOffset / text.length
  }
}

function itemRect(item, fontStyle, viewport, startOffset, endOffset) {
  if (!Array.isArray(item.transform) || !(item.width > 0) || !item.str?.length) return null
  const [, , , , originX, originY] = item.transform
  const textHeight = item.height || Math.hypot(item.transform[2], item.transform[3]) || Math.hypot(item.transform[0], item.transform[1])
  const ascent = Number.isFinite(fontStyle?.ascent) ? fontStyle.ascent : DEFAULT_FONT_ASCENT
  const descent = Number.isFinite(fontStyle?.descent) ? fontStyle.descent : DEFAULT_FONT_DESCENT
  const full = normalizeRect(
    viewport.convertToViewportRectangle([originX, originY + descent * textHeight, originX + item.width, originY + ascent * textHeight])
  )
  const { startRatio, endRatio } = measuredOffsets(item.str, startOffset, endOffset, fontStyle)
  const portion = endRatio - startRatio

  if (viewport.rotation === 90) {
    return { x: full.x, y: full.y + full.h * startRatio - HIT_PADDING_POINTS, w: full.w, h: full.h * portion + HIT_PADDING_POINTS * 2 }
  }
  if (viewport.rotation === 180) {
    return { x: full.x + full.w * (1 - endRatio) - HIT_PADDING_POINTS, y: full.y, w: full.w * portion + HIT_PADDING_POINTS * 2, h: full.h }
  }
  if (viewport.rotation === 270) {
    return { x: full.x, y: full.y + full.h * (1 - endRatio) - HIT_PADDING_POINTS, w: full.w, h: full.h * portion + HIT_PADDING_POINTS * 2 }
  }
  return { x: full.x + full.w * startRatio - HIT_PADDING_POINTS, y: full.y, w: full.w * portion + HIT_PADDING_POINTS * 2, h: full.h }
}

export async function findDocumentMatches(pages, query) {
  const needle = query.trim().toLocaleLowerCase()
  if (!needle) return []

  const matches = []
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const page = pages[pageIndex]
    const content = await page.getTextContent()
    const items = (content?.items ?? []).filter(item => item.str)
    const ranges = []
    let text = ''
    items.forEach((item, index) => {
      if (index > 0) text += ' '
      const start = text.length
      text += item.str
      ranges.push({ item, start, end: text.length })
    })

    const haystack = text.toLocaleLowerCase()
    const viewport = page.getViewport({ scale: 1 })
    let from = 0
    while (from <= haystack.length - needle.length) {
      const start = haystack.indexOf(needle, from)
      if (start < 0) break
      const end = start + needle.length
      const rects = ranges
        .filter(range => range.end > start && range.start < end)
        .map(range =>
          itemRect(
            range.item,
            content.styles?.[range.item.fontName],
            viewport,
            Math.max(start, range.start) - range.start,
            Math.min(end, range.end) - range.start
          )
        )
        .filter(Boolean)
      if (rects.length) matches.push({ page: pageIndex, rects })
      from = start + Math.max(needle.length, 1)
    }
  }
  return matches
}
